import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormField, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslatePipe } from '@ngx-translate/core';
import { PASSWORD_MIN_LENGTH } from '@rms/auth';
import { NotificationService } from '@rms/core/notification';
import { CloseButtonComponent } from '@rms/sdk/dialog';
import { AppError } from '@rms/sdk/errors';
import { ControlErrorDirective } from '@rms/sdk/forms/error';
import { MatchValidator, PasswordValidator } from '@rms/sdk/validators';
import { ToastComponent } from '@rms/ui/toast';
import { BehaviorSubject, finalize } from 'rxjs';

import { PartnerService } from '../../services';
import { PartnerUserPasswordData } from '../partner-user-dialog.model';

@Component({
  selector: 'rms-partner-user-password',
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    MatDialogClose,
    MatFormField,
    MatHint,
    MatSuffix,
    MatInput,
    MatButton,
    MatIconButton,
    MatIcon,
    MatProgressSpinner,
    TranslatePipe,
    CloseButtonComponent,
    ControlErrorDirective,
    ToastComponent,
  ],
  templateUrl: './partner-user-password.component.html',
})
export class PartnerUserPasswordComponent {
  protected readonly data = inject<PartnerUserPasswordData>(MAT_DIALOG_DATA);
  protected readonly loading$ = new BehaviorSubject<boolean>(false);
  protected readonly error$ = new BehaviorSubject<string | null>(null);
  protected readonly passwordVisible$ = new BehaviorSubject<boolean>(false);
  protected readonly repeatVisible$ = new BehaviorSubject<boolean>(false);
  protected readonly form = inject(FormBuilder).nonNullable.group(
    {
      password: [
        '',
        [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH), PasswordValidator.StrongPassword],
      ],
      repeat: ['', [Validators.required, Validators.minLength(PASSWORD_MIN_LENGTH)]],
    },
    {
      validators: MatchValidator.Fields('password', 'repeat', 'auth.controls.repeat-password.error-mismatch'),
    }
  );

  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject<MatDialogRef<PartnerUserPasswordComponent, boolean>>(MatDialogRef);
  private readonly partnerService = inject(PartnerService);
  private readonly notificationService = inject(NotificationService);

  protected submit(): void {
    if (this.form.invalid) {
      return;
    }

    const { partnerId, user } = this.data;
    this.error$.next(null);
    this.loading$.next(true);

    this.partnerService
      .setPartnerUserPassword(partnerId, user.id, this.form.controls.password.value)
      .pipe(
        finalize(() => this.loading$.next(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.notificationService.notify('partners.users.password.success', 'common.ok');
          this.dialogRef.close(true);
        },
        error: (error: AppError) => this.error$.next(error.message),
      });
  }
}
